import { attachCartRecommendationAnalytics } from './bd-cart-recommendation-analytics-v76.js?v=d02818fbed7c';
import { prioritizeCartSources, selectCartRecommendations } from './bd-cart-recommendations-v74.js?v=780ef65bf257';
import { attachRecommendationAnalytics } from './bd-recommendation-analytics.js';
import { getDeliveryMessage } from './bd-delivery.js';
import { embedConsentAllowed } from './bd-embed-consent.js';
import { getProductDisplayName } from 'bd-product-naming';
import { getProductTechnology, getLaptopSpecifications, getTabletSpecifications, getMonitorSpecifications, getPrinterSpecifications, getLaptopProcessorBadge } from 'bd-product-technology';

const productInput = (element) => ({
  title: element.dataset.title || '',
  brand: element.dataset.brand || '',
  category: element.dataset.category || '',
  description: element.dataset.description || '',
});

class BDProductName extends HTMLElement {
  connectedCallback() {
    try { this.textContent = getProductDisplayName(productInput(this)); }
    catch { /* The original server-rendered title is the safe fallback. */ }
  }
}

// Only whole category links enter the tab order. Remaining destinations stay in All categories.
class BDNavigation extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-category-shortcuts]');
    if (!this.rail) return;
    this.links = [...this.rail.querySelectorAll('a')];
    this.onFocusIn = (event) => { this.lastFocused = this.links.includes(event.target) ? event.target : null; };
    document.addEventListener('focusin', this.onFocusIn);
    this.scheduleFit = () => {
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = requestAnimationFrame(() => this.fit());
    };
    this.observer = new ResizeObserver(this.scheduleFit);
    this.observer.observe(this);
    this.observer.observe(this.rail);
    document.fonts?.ready.then(() => { if (this.isConnected) this.scheduleFit(); });
    this.scheduleFit();
  }
  fit() {
    const pinned = this.querySelector(':scope > a');
    if (pinned) {
      pinned.hidden = false;
      const style = getComputedStyle(this);
      const contentWidth = this.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const required = this.querySelector('summary').getBoundingClientRect().width + pinned.getBoundingClientRect().width + (parseFloat(style.columnGap) || 0);
      if (required > contentWidth) {
        if (document.activeElement === pinned) this.querySelector('summary').focus({ preventScroll: true });
        pinned.hidden = true;
      }
    }
    const available = this.rail.clientWidth;
    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    // Measuring synchronously does not expose partially fitted links for a frame.
    this.links.forEach((link) => { link.hidden = false; });
    const widths = this.links.map((link) => link.getBoundingClientRect().width);
    let used = 0;
    let count = 0;
    for (const width of widths) {
      const next = used + (count ? gap : 0) + width;
      if (!width || next > available + .1) break;
      used = next;
      count += 1;
    }
    const focused = document.activeElement === document.body ? this.lastFocused : document.activeElement;
    const focusedIndex = this.links.indexOf(focused);
    this.links.forEach((link, index) => { link.hidden = index >= count; });
    if (focusedIndex >= count) this.querySelector('summary')?.focus({ preventScroll: true });
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('focusin', this.onFocusIn);
    cancelAnimationFrame(this.fitFrame);
  }
}

// Product recommendations are optional: failed/empty responses leave checkout intact.
class BDProductDiscovery extends HTMLElement {
  connectedCallback() {
    this.stopAnalytics = attachRecommendationAnalytics(this);
    this.onClick = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && !tab.hidden) this.select(tab.dataset.intent);
    };
    this.onKeyDown = (event) => {
      if (!event.target.matches('[role="tab"]')) return;
      const tabs = [...this.querySelectorAll('[role="tab"]')].filter((tab) => !tab.hidden);
      const index = tabs.indexOf(event.target);
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (next === undefined || !tabs.length) return;
      event.preventDefault();
      this.select(tabs[next].dataset.intent);
      tabs[next].focus();
    };
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
    }
  }
  select(intent) {
    this.activeIntent = intent;
    this.querySelectorAll('[role="tab"]').forEach((tab) => {
      const active = !tab.hidden && tab.dataset.intent === intent;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.querySelectorAll('[data-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.panel !== intent;
    });
  }
  async refresh() {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const intents = ['related', 'complementary'];
    const limit = Math.min(10, Math.max(1, Number(this.dataset.limit) || 6));
    const hadFocus = this.contains(document.activeElement);
    this.setAttribute('aria-busy', 'true');
    try {
      const [cartResult, ...results] = await Promise.allSettled([
        fetch(`${window.Shopify.routes.root}cart.js`, { signal: controller.signal }).then(async (response) => {
          if (!response.ok) throw new Error('Cart unavailable');
          return response.json();
        }),
        ...intents.map(async (intent) => {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('product_id', this.dataset.productId);
          url.searchParams.set('intent', intent);
          url.searchParams.set('limit', '10');
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) return { cards: [], compact: [] };
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          return {
            cards: [...document.querySelectorAll('[data-response-items] > [data-recommendation-id]')],
            compact: intent === 'complementary' ? [...document.querySelectorAll('[data-compact-response] > [data-recommendation-id]')] : [],
          };
        }),
      ]);
      if (controller.signal.aborted) return;
      // Fresh cart state is needed before showing additions; requests are optional and fail closed.
      if (cartResult.status !== 'fulfilled' || !Array.isArray(cartResult.value.items)) {
        this.hidden = true;
        this.hideCompact();
        return;
      }
      const excluded = new Set([this.dataset.productId, ...cartResult.value.items.map((item) => String(item.product_id))]);
      const compactSlot = [...document.querySelectorAll('bd-product-complements')].find(slot => slot.dataset.productId === this.dataset.productId);
      const compactSeen = new Set(excluded);
      const compactCandidates = results[1].status === 'fulfilled' ? results[1].value.compact : [];
      const compactItems = compactSlot ? compactCandidates.filter(item => {
        const id = item.dataset.recommendationId;
        if (!id || compactSeen.has(id) || item.dataset.available !== 'true') return false;
        compactSeen.add(id);
        return true;
      }).slice(0, 2) : [];
      if (compactSlot) {
        const compactHadFocus = compactSlot.contains(document.activeElement);
        compactSlot.querySelector('[data-compact-complements]').replaceChildren(...compactItems);
        compactSlot.hidden = !compactItems.length;
        if (compactHadFocus && document.activeElement === document.body) {
          const next = compactSlot.querySelector('a,button') || this.closest('main')?.querySelector('[id^="ProductSubmitButton-"]');
          next?.focus({ preventScroll: true });
        }
      }
      const compactIds = new Set(compactItems.map(item => item.dataset.recommendationId));
      const available = [];
      intents.forEach((intent, index) => {
        const seen = new Set([...excluded, ...compactIds]);
        const candidates = results[index].status === 'fulfilled' ? results[index].value.cards : [];
        const items = candidates.filter((item) => {
          const id = item.dataset.recommendationId;
          if (!id || seen.has(id) || item.dataset.available !== 'true') return false;
          seen.add(id);
          return true;
        }).slice(0, limit);
        this.querySelector(`[data-panel="${intent}"] ul`).replaceChildren(...items);
        this.querySelector(`[role="tab"][data-intent="${intent}"]`).hidden = !items.length;
        if (items.length) available.push(intent);
      });
      this.hidden = !available.length;
      const selected = available.includes(this.activeIntent) ? this.activeIntent : available[0];
      this.select(selected);
      if (hadFocus && !this.hidden && (document.activeElement === document.body || document.activeElement.hidden)) {
        this.querySelector(`[role="tab"][data-intent="${selected}"]`).focus();
      }
    } catch { if (!controller.signal.aborted) { this.hidden = true; this.hideCompact(); } }
    finally { if (!controller.signal.aborted) this.removeAttribute('aria-busy'); }
  }
  hideCompact() {
    document.querySelectorAll('bd-product-complements').forEach(slot => {
      if (slot.dataset.productId === this.dataset.productId) {
        slot.hidden = true;
        slot.querySelector('[data-compact-complements]')?.replaceChildren();
      }
    });
  }
  disconnectedCallback() {
    this.stopAnalytics?.();
    this.controller?.abort();
    this.unsubscribe?.();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
  }
}

class BDCartComplements extends HTMLElement {
  connectedCallback() {
    this.stopAnalytics = attachCartRecommendationAnalytics(this);
    this.refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
    }
  }
  disconnectedCallback() {
    this.stopAnalytics?.();
    this.controller?.abort();
    this.controller = null;
    this.unsubscribe?.();
  }
  async refresh() {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const current = () => this.controller === controller && !controller.signal.aborted && this.isConnected;
    this.hidden = true;
    try {
      const root = window.Shopify.routes.root;
      const sectionUrl = new URL(this.dataset.cartUrl || `${root}cart`, window.location.origin);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
      const [cartResult, sectionResult, factsResult] = await Promise.allSettled([
        fetch(`${root}cart.js`, { signal: controller.signal }).then(async response => {
          if (!response.ok) throw new Error('Cart unavailable');
          return response.json();
        }),
        fetch(sectionUrl, { signal: controller.signal }).then(async response => {
          if (!response.ok) throw new Error('Cart rule section unavailable');
          return new DOMParser().parseFromString(await response.text(), 'text/html');
        }),
        this.dataset.factsUrl ? fetch(this.dataset.factsUrl, { signal: controller.signal, cache: 'force-cache' }).then(async response => {
          if (!response.ok) return {};
          const data = await response.json();
          return data.version === 'v76' && data.products && typeof data.products === 'object' ? data.products : {};
        }) : Promise.resolve({}),
      ]);
      if (!current() || cartResult.status !== 'fulfilled' || !Array.isArray(cartResult.value.items)) return;
      const cart = cartResult.value.items;
      this.stopAnalytics?.confirm?.(cart);
      if (!cart.length || cart.some(item => !item.product_id)) return;
      const cartIds = new Set(cart.map(item => String(item.product_id)));
      const freshSection = sectionResult.status === 'fulfilled' ? sectionResult.value : null;
      const json = freshSection?.querySelector('[data-cart-rule-data]');
      let snapshot;
      try { snapshot = json ? JSON.parse(json.textContent) : null; } catch { snapshot = null; }
      if (snapshot) {
        if (!Array.isArray(snapshot.cart) || !Array.isArray(snapshot.rules)) return;
        const factIds = new Set(snapshot.cart.map(item => String(item.id)));
        if (factIds.size !== cartIds.size || [...cartIds].some(id => !factIds.has(id))) return;
      } else if (this.dataset.ruleRefresh === 'true') return;
      const reviewed = factsResult.status === 'fulfilled' ? factsResult.value : {};
      const withReviewedFacts = product => {
        const facts = reviewed[String(product.id)] || {};
        const merged = { ...product };
        for (const key of ['interfaces', 'compatibilityKeys', 'includedFamilies']) {
          merged[key] = [...new Set([...(Array.isArray(product[key]) ? product[key] : []), ...(Array.isArray(facts[key]) ? facts[key] : [])])];
        }
        for (const key of ['screenMin', 'screenMax']) if (Number(facts[key]) > 0) merged[key] = facts[key];
        if (!merged.family && typeof facts.family === 'string') merged.family = facts.family;
        return merged;
      };
      const cartProducts = (snapshot?.cart || cart.map(item => ({ ...item, id: item.product_id, title: item.product_title || item.title || '', category: item.product_type || '' }))).map(withReviewedFacts);
      const rules = snapshot?.rules || [];
      const sourceIds = prioritizeCartSources(cartProducts, rules);
      const ruleCards = [...(freshSection?.querySelector('[data-cart-rule-cards]')?.content?.querySelectorAll('[data-recommendation-id]') || [])];
      const results = await Promise.allSettled(sourceIds.map(async sourceId => {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('product_id', sourceId);
        url.searchParams.set('intent', 'complementary');
        url.searchParams.set('limit', '10');
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return [];
        const result = new DOMParser().parseFromString(await response.text(), 'text/html');
        return [...result.querySelectorAll('[data-recommendation-items] > [data-recommendation-id]')].map(element => ({ element, sourceId }));
      }));
      if (!current()) return;
      const candidate = (element, sourceId = '') => {
        let facts = {};
        try { facts = JSON.parse(element.querySelector('[data-product-facts]')?.textContent || '{}'); } catch { /* Malformed optional facts are never eligibility. */ }
        return withReviewedFacts({ ...facts,
          id: element.dataset.recommendationId, available: element.dataset.available === 'true',
          title: element.dataset.productTitle || '', family: element.dataset.family || facts.family || '',
          ruleId: element.dataset.ruleId || '', sourceId, element,
        });
      };
      const candidates = [
        ...ruleCards.map(element => candidate(element)),
        ...results.flatMap(result => result.status === 'fulfilled' ? result.value.map(({ element, sourceId }) => candidate(element, sourceId)) : []),
      ];
      const target = this.querySelector('[data-recommendation-items]');
      if (!target) return;
      const render = () => {
        if (!current()) return;
        const selected = selectCartRecommendations(cartProducts, rules, candidates, 2);
        const elements = selected.map(item => item.element);
        const unchanged = elements.length === target.children.length && elements.every((element, index) => element === target.children[index]);
        // A late ranking response must not remove the control a shopper is using.
        if (!unchanged && target.contains(document.activeElement)) return;
        selected.forEach((item, index) => {
          item.element.dataset.sourceId = item.sourceId;
          item.element.dataset.position = String(index + 1);
          const reason = item.element.querySelector('[data-recommendation-reason]');
          if (reason) { reason.textContent = item.reason; reason.hidden = !item.reason; }
          if (item.recommendationUrl) item.element.querySelectorAll('a[href]').forEach(link => { link.href = item.recommendationUrl; });
        });
        if (!unchanged) target.replaceChildren(...elements);
        this.hidden = selected.length === 0;
      };
      // Show valid group offers before optional purchase-pattern requests finish.
      render();
      if (this.dataset.purchaseSignals === 'true' && candidates.some(item => item.ruleId)) {
        const signals = await Promise.allSettled(sourceIds.map(async sourceId => {
          const url = new URL(`${root}recommendations/products.json`, window.location.origin);
          url.searchParams.set('product_id', sourceId);
          url.searchParams.set('intent', 'related');
          url.searchParams.set('limit', '10');
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) return [];
          const data = await response.json();
          return Array.isArray(data.products) ? data.products : [];
        }));
        if (!current()) return;
        signals.forEach(result => {
          if (result.status !== 'fulfilled') return;
          result.value.forEach((product, index) => {
            let url;
            try { url = new URL(product.url, window.location.origin); } catch { return; }
            if (url.origin !== window.location.origin || !url.pathname.startsWith(`${root}products/`) || url.searchParams.get('pr_prod_strat') !== 'copurchase') return;
            candidates.filter(item => item.ruleId && String(item.id) === String(product.id)).forEach(item => {
              if (!item.copurchaseRank || item.copurchaseRank > index + 1) { item.copurchaseRank = index + 1; item.recommendationUrl = url.href; }
            });
          });
        });
        render();
      }
    } catch { /* Optional additions cannot interrupt quantities, fulfillment or checkout. */ }
  }
}

// Only labeled source specifications are used. Unknown or contradictory facts stay absent.
class BDProductTechnology extends HTMLElement {
  connectedCallback() {
    try {
      const input = productInput(this);
      const specifications = getLaptopSpecifications(input, this.dataset);
      if (specifications) {
        this.renderSpecifications(specifications);
        return;
      }
      const tabletSpecifications = getTabletSpecifications(input, this.dataset);
      if (tabletSpecifications) {
        this.renderSpecifications(tabletSpecifications, 'tablets');
        return;
      }
      const monitorSpecifications = getMonitorSpecifications(input, this.dataset);
      if (monitorSpecifications) {
        this.renderSpecifications(monitorSpecifications, 'monitors');
        return;
      }
      const printerSpecifications = getPrinterSpecifications(input, this.dataset);
      if (printerSpecifications) {
        this.renderSpecifications(printerSpecifications, 'printers');
        return;
      }
      const badges = getProductTechnology(input);
      if (!badges.length) return;
      const heading = document.createElement('h2');
      heading.className = 'h3';
      heading.textContent = 'Inside this upgrade';
      heading.className = 'visually-hidden';
      this.replaceChildren(heading);
      const list = document.createElement('div');
      list.className = 'bd-tech-facts';
      badges.forEach((item) => {
        const badge = document.createElement('span');
        badge.className = `bd-tech-fact${item.logoTone === 'light' ? ' bd-tech-fact--dark' : ''}`;
        const key = item.id.split('-').at(-1);
        const asset = this.dataset[`${key}Logo`];
        if (item.logo && asset) {
          const image = document.createElement('img');
          image.src = asset;
          image.width = 54;
          image.height = 30;
          image.alt = item.brand;
          image.loading = 'lazy';
          image.addEventListener('error', () => image.remove(), { once: true });
          badge.append(image);
        }
        const label = document.createElement('span');
        label.className = 'bd-tech-fact__copy';
        const kind = document.createElement('small');
        kind.textContent = item.kind === 'processor' ? 'Processor' : 'Operating system';
        const value = document.createElement('span');
        value.textContent = item.label;
        label.append(kind, value);
        badge.append(label);
        list.append(badge);
      });
      this.append(list);
      const credit = badges.find((item) => item.attribution);
      if (credit) {
        const attribution = document.createElement('details');
        attribution.className = 'bd-brand-credit';
        const summary = document.createElement('summary');
        summary.textContent = 'Brand credits';
        const text = document.createElement('p');
        text.textContent = credit.attribution;
        const link = document.createElement('a');
        link.href = 'https://creativecommons.org/licenses/by/3.0/';
        link.textContent = 'Creative Commons 3.0 Attribution License';
        attribution.append(summary, text, link);
        this.append(attribution);
      }
      this.hidden = false;
    } catch { /* Do not guess technical specifications. */ }
  }

  renderSpecifications(specifications, category = 'laptops') {
    this.dataset.specificationLayout = category;
    const heading = document.createElement('h2');
    heading.className = 'bd-tech-heading';
    heading.textContent = 'At a glance';
    const list = document.createElement('dl');
    list.className = 'bd-tech-facts bd-tech-facts--specifications';
    const categoryNames = { laptops: 'Laptop', tablets: 'Tablet', monitors: 'Monitor', printers: 'Printer' };
    list.setAttribute('aria-label', `${categoryNames[category] || 'Product'} specifications`);
    const paths = {
      processor: ['M9 3v3m6-3v3M9 18v3m6-3v3M3 9h3m-3 6h3m12-6h3m-3 6h3', 'M6 6h12v12H6z', 'M9 9h6v6H9z'],
      memory: ['M3 6h18v10H3z', 'M6 10h3m3 0h3m3 0h1', 'M6 16v3m4-3v3m4-3v3m4-3v3'],
      storage: ['M5 4h14l2 11v5H3v-5L5 4z', 'M3 15h18', 'M16 18h1m2 0h1'],
      display: ['M3 4h18v13H3z', 'M8 21h8m-4-4v4'],
      os: ['M3 4h18v16H3z', 'M3 9h18', 'M6 6.5h.1m2.9 0h.1'],
      size: ['M3 4h18v13H3z', 'M8 21h8m-4-4v4', 'M6 7h3m-3 0v3m12 4h-3m3 0v-3', 'M6 7l12 7'],
      resolution: ['M3 4h18v16H3z', 'M3 9h18M3 15h18M9 4v16M15 4v16'],
      panel: ['M4 5h16v14H4z', 'M8 5v14m4-14v14m4-14v14'],
      refresh: ['M20 7v5h-5', 'M4 17v-5h5', 'M19 11a7 7 0 0 0-12-5L4 9m1 4a7 7 0 0 0 12 5l3-3'],
      ports: ['M8 3v4m8-4v4', 'M6 7h12v6a6 6 0 0 1-12 0V7z', 'M12 19v3'],
      printTechnology: ['M6 8V3h12v5', 'M6 16H3V8h18v8h-3', 'M6 13h12v8H6z', 'M17 11h1'],
      printOutput: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M12 3v18', 'M15 7h1m-1 5h2m-2 5h1'],
      functions: ['M8 3h10v14H8z', 'M5 7H3v14h11v-2', 'M11 7h4m-4 4h4'],
      connectivity: ['M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-2 2', 'M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l2-2'],
      voltage: ['M13 2L4 14h7l-1 8 10-12h-7l1-8z'],
    };
    const shortTitles = { processor: 'CPU', memory: 'RAM', storage: 'Storage', display: 'Display', os: 'OS', size: 'Size', resolution: 'Resolution', panel: 'Panel', refresh: 'Refresh', ports: 'Ports', printTechnology: 'Print', printOutput: 'Output', functions: 'Functions', connectivity: 'Connect', voltage: 'Voltage' };
    // The compact strip keeps the full saved value available to assistive
    // technology and on hover. Only the visual presentation is shortened.
    const valueLines = (item) => {
      if (item.kind === 'memory') return [item.label.replace(/\s+RAM$/, '')];
      if (item.kind === 'storage') {
        const match = item.label.match(/^(.*)\s+(SSD|HDD)$/);
        return match ? [match[1], match[2]] : [item.label];
      }
      if (item.kind === 'display') return item.label.split(' · ').map(line => line.replace(/\s*×\s*/g, '×'));
      if (item.kind === 'os') {
        const match = item.label.match(/^(Windows|Fire OS)\s+(.+)$/i);
        return match ? [match[1], match[2]] : [item.label];
      }
      if (item.kind === 'resolution') return [item.label.replace(/\s*×\s*/g, '×')];
      if (item.kind === 'refresh') {
        const overclock = item.label.match(/(\d+(?:\.\d+)?)\s*Hz\s*(?:\([^)]*?\boverclock(?:ed|ing)?\b|(?:,|-|—|via|with|when|in|at)*\s*overclock(?:ed|ing)?\b)/i);
        if (overclock) return [`${overclock[1]} Hz`, 'OC'];
        const displayPort = item.label.match(/(\d+(?:\.\d+)?)\s*Hz\s*(?:\(|,|-|—|via|with|over|using)*\s*DisplayPort\b/i);
        if (displayPort) return [`${displayPort[1]} Hz`, 'DP'];
        const maximum = item.label.match(/(\d+(?:\.\d+)?)\s*Hz\s*max(?:imum)?\b/i);
        if (maximum) return [`${maximum[1]} Hz`, 'Max'];
        return [item.label];
      }
      if (item.kind === 'ports' || item.kind === 'connectivity') {
        // Counts and protocol versions remain in the accessible full value.
        // The narrow strip summarizes only interfaces explicitly present.
        const interfaceText = item.kind === 'ports' ? item.label.replace(/\([^)]*\)/g, '') : item.label;
        const interfaces = interfaceText.match(/USB(?:-[ABC])?|(?:mini|micro)[-\s]?HDMI|HDMI|DisplayPort|VGA|DVI(?:-[DI])?|Wi-?Fi|Ethernet|Bluetooth|Thunderbolt(?:\s*\d+)?|3\.5\s*mm|headphone(?:\s*output)?|audio(?:\s*(?:output|input))?/gi);
        return interfaces ? [...new Set(interfaces.map(name => {
          if (/^DisplayPort$/i.test(name)) return 'DP';
          if (/^Wi-?Fi$/i.test(name)) return 'Wi-Fi';
          if (/^(?:3\.5|headphone|audio)/i.test(name)) return 'Audio';
          if (/^(?:mini|micro)[-\s]?HDMI$/i.test(name)) return name.replace(/^mini[-\s]?/i, 'Mini ').replace(/^micro[-\s]?/i, 'Micro ').replace(/hdmi$/i, 'HDMI');
          if (/^Thunderbolt/i.test(name)) return name.replace(/^Thunderbolt\s*/i, 'TB ');
          return name;
        }))] : [item.label];
      }
      if (item.kind === 'functions') {
        if (/^print labels$/i.test(item.label)) return ['Print', 'labels'];
        const functions = item.label.match(/\b(?:print|copy|scan|fax)\b/gi);
        return functions ? [...new Set(functions.map(name => name[0].toUpperCase() + name.slice(1).toLowerCase()))] : [item.label];
      }
      if (item.kind === 'printTechnology') {
        if (/^(?:refillable ink tanks?|inkjet with refillable tanks)$/i.test(item.label)) return ['Ink tank'];
        if (/^thermal transfer$/i.test(item.label)) return ['Thermal', 'transfer'];
        if (/^direct thermal$/i.test(item.label)) return ['Direct', 'thermal'];
      }
      if (item.kind === 'printOutput' && /^monochrome labels$/i.test(item.label)) return ['Mono', 'labels'];
      if (item.kind === 'printOutput' && /^black\s*(?:&|and)\s*white$/i.test(item.label)) return ['Black &', 'white'];
      if (item.kind === 'voltage') {
        if (/^Please confirm the power-input rating for the stocked unit\.$/i.test(item.label)) return ['Confirm', 'voltage'];
        const volts = item.label.match(/(\d+(?:\.\d+)?)\s*(?:[-–—]\s*(\d+(?:\.\d+)?))?\s*V\b/i);
        if (volts && /\bUSB\b/i.test(item.label) && Number(volts[1]) === 5) return ['USB', '5 V'];
        if (volts && /\bAC\b/i.test(item.label)) {
          const low = Number(volts[1]), high = Number(volts[2] || volts[1]);
          const mains = [110, 220].filter(nominal => low <= nominal && high >= nominal);
          if (mains.length) return mains.map(nominal => `${nominal} V`);
        }
        return [item.label];
      }
      if (['size', 'panel', 'printTechnology', 'printOutput'].includes(item.kind)) return [item.label];
      const cpu = item.label.replace(/^(?:Intel|AMD|MediaTek|Qualcomm|Apple)\s+/i, '');
      const generic = cpu.match(/^octa-core processor, up to (.+)$/i);
      if (generic) return ['8-core', `Up to ${generic[1]}`];
      const match = cpu.match(/^(Core(?: Ultra)?(?: i[3579]| [3579])?|Ryzen(?: AI)? \d|Helio|Dimensity|Snapdragon(?: X(?: Plus| Elite)?)?)[\s-]+(.+)$/i);
      return match ? [match[1], match[2]] : [cpu];
    };
    specifications.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'bd-tech-fact bd-tech-fact--specification';
      card.dataset.specification = item.kind;
      const visual = document.createElement('span');
      visual.className = 'bd-tech-fact__visual';
      visual.setAttribute('aria-hidden', 'true');
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('fill', 'none');
      icon.setAttribute('stroke', 'currentColor');
      icon.setAttribute('stroke-width', '2.2');
      icon.setAttribute('stroke-linecap', 'round');
      icon.setAttribute('stroke-linejoin', 'round');
      paths[item.kind].forEach((data) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', data);
        icon.append(path);
      });
      visual.append(icon);
      const label = document.createElement('dt');
      label.setAttribute('aria-label', item.title);
      const labelText = document.createElement('span');
      labelText.className = 'bd-tech-fact__label';
      labelText.textContent = shortTitles[item.kind];
      label.append(visual, labelText);
      const value = document.createElement('dd');
      value.setAttribute('aria-label', item.label);
      value.title = item.label;
      value.dataset.fullValue = item.label;
      valueLines(item).forEach((line) => {
        const text = document.createElement('span');
        text.setAttribute('aria-hidden', 'true');
        const resolution = ['display', 'resolution'].includes(item.kind) && line.match(/^(\d+)×(\d+)$/);
        if (resolution) {
          text.className = 'bd-tech-fact__resolution';
          text.append(`${resolution[1]}×`, document.createElement('wbr'), resolution[2]);
        } else text.textContent = line;
        value.append(text);
      });
      const fullValue = document.createElement('span');
      fullValue.className = 'visually-hidden';
      fullValue.textContent = item.label;
      value.append(fullValue);
      card.append(label, value);
      list.append(card);
    });
    const processorBadge = category === 'laptops' ? getLaptopProcessorBadge(productInput(this), this.dataset) : null;
    const badgeUrl = processorBadge && this.dataset[processorBadge.datasetKey];
    if (badgeUrl) {
      const header = document.createElement('div');
      header.className = 'bd-tech-header';
      const rail = document.createElement('span');
      rail.className = 'bd-tech-processor-badge';
      rail.dataset.processorBadge = processorBadge.id;
      const image = document.createElement('img');
      image.src = badgeUrl;
      image.alt = processorBadge.name;
      image.width = processorBadge.width;
      image.height = processorBadge.height;
      image.title = processorBadge.processor;
      image.decoding = 'async';
      image.addEventListener('error', () => {
        rail.remove();
        header.replaceWith(heading);
      }, { once: true });
      rail.append(image);
      header.append(heading, rail);
      this.replaceChildren(header, list);
    } else this.replaceChildren(heading, list);
    this.hidden = false;
  }
}


class BDDeliveryMessage extends HTMLElement {
  connectedCallback() {
    this.refresh = () => {
      clearTimeout(this.timer);
      const now = new Date();
      this.textContent = getDeliveryMessage(now, this.dataset.kind || 'physical', this.dataset.format || 'full');
      // Curaçao is UTC−4 all year. Refresh at the shared day boundary.
      const localDayMs = ((now.getTime() - 14400000) % 86400000 + 86400000) % 86400000;
      this.timer = setTimeout(this.refresh, 86400000 - localDayMs + 20);
    };
    this.refresh();
    document.addEventListener('visibilitychange', this.refresh);
  }
  disconnectedCallback() { clearTimeout(this.timer); document.removeEventListener('visibilitychange', this.refresh); }
}

class BDStoreMap extends HTMLElement {
  connectedCallback() {
    this.loadButton = this.querySelector('[data-map-load]');
    this.closeButton = this.querySelector('[data-map-close]');
    this.mapSlot = this.querySelector('[data-map-slot]');
    this.status = this.querySelector('[data-map-status]');
    this.onLoad = () => this.load();
    this.onClose = () => this.close(true);
    this.onKey = event => { if (event.key === 'Escape' && this.contains(document.activeElement)) this.close(true); };
    this.onConsent = () => { if (!embedConsentAllowed()) this.close(this.contains(document.activeElement)); };
    this.loadButton.addEventListener('click', this.onLoad);
    this.closeButton.addEventListener('click', this.onClose);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('visitorConsentCollected', this.onConsent);
    document.addEventListener('customerPrivacyApiLoaded', this.onConsent);
  }
  load() {
    this.status.hidden = true;
    if (!embedConsentAllowed()) {
      this.status.textContent = 'Your privacy preferences do not allow an embedded map. Use the Google Maps link below.';
      this.status.hidden = false;
      return;
    }
    if (!this.dataset.query?.trim()) return;
    const frame = document.createElement('iframe');
    frame.src = `https://www.google.com/maps?q=${encodeURIComponent(this.dataset.query)}&output=embed`;
    frame.title = this.dataset.title || 'Better Deals store map';
    frame.loading = 'lazy';
    frame.referrerPolicy = 'no-referrer-when-downgrade';
    frame.allowFullscreen = true;
    this.mapSlot.replaceChildren(frame);
    this.classList.add('is-active');
    this.mapSlot.hidden = false;
    this.loadButton.hidden = true;
    this.closeButton.hidden = false;
    this.closeButton.focus();
  }
  close(restoreFocus = false) {
    this.classList.remove('is-active');
    this.mapSlot?.replaceChildren();
    if (this.mapSlot) this.mapSlot.hidden = true;
    if (this.loadButton) this.loadButton.hidden = false;
    if (this.closeButton) this.closeButton.hidden = true;
    if (restoreFocus && this.isConnected) this.loadButton?.focus();
  }
  disconnectedCallback() {
    this.close(false);
    this.loadButton?.removeEventListener('click', this.onLoad);
    this.closeButton?.removeEventListener('click', this.onClose);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('visitorConsentCollected', this.onConsent);
    document.removeEventListener('customerPrivacyApiLoaded', this.onConsent);
  }
}


// Select once per tab session. No timers, navigation controls or changes while browsing.
class BDBrandSelection extends HTMLElement {
  connectedCallback() {
    if (this.selected || this.dataset.editor === 'true' || this.classList.contains('bd-brands--product')) return;
    this.selected = true;
    const items = [...this.querySelectorAll('[data-brand-item]')];
    const unique = [...new Map(items.map(item => [item.dataset.brandKey, item])).values()];
    const keys = unique.map(item => item.dataset.brandKey);
    const storageKey = `bd-brand-selection-v1:${this.dataset.sectionId || 'home'}`;
    let previous = [];
    try {
      const stored = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) previous = [...new Set(stored.filter(key => typeof key === 'string' && keys.includes(key)))];
    } catch { /* Storage is optional; the shelf still works. */ }
    const remaining = keys.filter(key => !previous.includes(key));
    for (let i = remaining.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
    }
    const selectedKeys = [...previous, ...remaining].slice(0, 8);
    items.forEach(item => { item.hidden = !selectedKeys.includes(item.dataset.brandKey) || !unique.includes(item); });
    try { sessionStorage.setItem(storageKey, JSON.stringify(selectedKeys)); } catch { /* No persistence is required. */ }
  }
}

for (const [name, component] of [
  ['bd-brand-selection', BDBrandSelection],
  ['bd-delivery-message', BDDeliveryMessage],
  ['bd-store-map', BDStoreMap],
  ['bd-navigation', BDNavigation],
  ['bd-product-name', BDProductName],
  ['bd-product-discovery', BDProductDiscovery],
  ['bd-cart-complements', BDCartComplements],
  ['bd-product-technology', BDProductTechnology],
]) if (!customElements.get(name)) customElements.define(name, component);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const menu = document.querySelector('.bd-category-menu[open]');
  if (!menu) return;
  menu.open = false;
  menu.querySelector('summary').focus({ preventScroll: true });
});
document.addEventListener('click', (event) => {
  const menu = document.querySelector('.bd-category-menu[open]');
  if (menu && !menu.contains(event.target)) menu.open = false;
});


// Keep the native editable sections and keyboard sequence aligned at the desktop breakpoint.
const bdDesktopOffers = window.matchMedia('(min-width: 1100px)');
let bdOfferLastFocused = null;
document.addEventListener('focusin', event => { bdOfferLastFocused = event.target.closest?.('.bd-hero-support, .bd-monthly-promoted') ? event.target : null; });
document.addEventListener('pointerdown', event => { if (!event.target.closest?.('.bd-hero-support, .bd-monthly-promoted')) bdOfferLastFocused = null; });
function arrangeHomepageOffers() {
  const hero = document.querySelector('.bd-intro--offer-grid');
  const monthly = document.querySelector('.bd-products--monthly')?.closest('.shopify-section');
  const benefits = document.querySelector('.bd-trust')?.closest('.shopify-section');
  if (!hero || !monthly || !benefits || monthly === benefits || monthly.parentNode !== benefits.parentNode) return;
  // Some browsers blur a just-hidden link before the media-query event arrives.
  const focused = document.activeElement === document.body && bdOfferLastFocused?.isConnected ? bdOfferLastFocused : document.activeElement;
  if (bdDesktopOffers.matches && benefits.nextElementSibling !== monthly) monthly.before(benefits);
  else if (!bdDesktopOffers.matches && monthly.nextElementSibling !== benefits) monthly.after(benefits);
  if (benefits.contains(focused)) focused.focus({preventScroll:true});
  const hiddenCard = focused?.closest(bdDesktopOffers.matches ? '.bd-monthly-promoted' : '.bd-hero-support');
  if (hiddenCard) {
    const link = hiddenCard.querySelector('a');
    const visibleGroup = bdDesktopOffers.matches ? hero : monthly;
    const replacement = [...visibleGroup.querySelectorAll('a')].find(candidate => candidate.href === link?.href && candidate.getClientRects().length);
    replacement?.focus({preventScroll:true});
  }
}
bdDesktopOffers.addEventListener('change', arrangeHomepageOffers);
document.addEventListener('shopify:section:load', arrangeHomepageOffers);
arrangeHomepageOffers();
